// src/pages/VehicleV2/BillAddVehicle.jsx
// Design aligned with ProductionBOMListing
// Writes progressIds + vehDetails into billGen_selection_${vehicleId}
// and sessionStorage billGeneration_selectedVehDetails

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaArrowLeft,
  FaSearch,
  FaFileInvoiceDollar,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";
const API_BASE = `${BASE_URL}/api/billAddVehicle`;

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};
const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();
const isDDMMYYYY = (s) =>
  typeof s === "string" && /^(\d{2})\/(\d{2})\/(\d{4})$/.test(s);
const todayDDMMYYYY = () => {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};
const parseDDMMYYYY = (s) => {
  if (!isDDMMYYYY(s)) return null;
  const [dd, mm, yyyy] = s.split("/").map((x) => Number(x));
  const dt = new Date(yyyy, mm - 1, dd);
  if (dt.getFullYear() !== yyyy || dt.getMonth() !== mm - 1 || dt.getDate() !== dd) return null;
  dt.setHours(0, 0, 0, 0);
  return dt;
};

const defaultFilters = { fromDate: "", toDate: "" };

const columns = [
  { key: "date", label: "Date" },
  { key: "startingHrs", label: "Starting Hrs" },
  { key: "closingHrs", label: "Closing Hrs" },
  { key: "breakingHrs", label: "Breaking Hrs" },
  { key: "workingHrs", label: "Working Hrs" },
  { key: "cumulativeHrs", label: "Cumulative Hrs" },
  { key: "hourRent", label: "Hour Rent" },
  { key: "hourAmount", label: "Hour Amount" },
  { key: "hourCumulativeAmount", label: "Hour Cumulative Amount" },
  { key: "hourAdvanceAmount", label: "Hour Advance Amount" },
  { key: "dieselQty", label: "Diesel Qty" },
  { key: "startingKm", label: "Starting Km" },
  { key: "closingKm", label: "Closing Km" },
  { key: "workingKm", label: "Working Km" },
  { key: "cumulativeKm", label: "Cumulative Km" },
  { key: "rent", label: "Rent" },
  { key: "kmAmount", label: "Km Amount" },
  { key: "kmCumulativeAmount", label: "Km Cumulative Amount" },
  { key: "noOfLoads", label: "No.Of loads" },
];

const BillAddVehicle = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "0";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";

  const vehId =
    toInt(params.get("VehID")) ||
    toInt(params.get("Vehid")) ||
    toInt(params.get("vehId")) ||
    toInt(params.get("vehicleId")) ||
    0;

  const rawType = (params.get("Type") || params.get("type") || params.get("brtype") || "H").toUpperCase();
  const vehType = rawType === "O" ? "O" : "H";

  const STORAGE_KEY = useMemo(() => `billGen_selection_${vehId}`, [vehId]);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const [pageLoading, setPageLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);

  const [header, setHeader] = useState({
    vehicleNumber: "",
    period: "",
    toDate: "",
    basis: "",
    vehicleType: "",
  });
  const [filters, setFilters] = useState(defaultFilters);
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState({});

  const isBusy = pageLoading || gridLoading || billingLoading;

  const fieldClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;
  const labelClass = `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`;
  const panelClass = `p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`;
  const panelTitleClass = `text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`;
  const ghostBtn = `flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
    isDark
      ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
  }`;
  const primaryBtn = `flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`;

  const canUse = useCallback((cond, msg) => {
    if (!cond) toast.error(msg);
    return !!cond;
  }, []);

  const validateDates = useCallback(() => {
    const { fromDate, toDate } = filters;
    if (fromDate && !isDDMMYYYY(fromDate)) return canUse(false, "From date must be dd/MM/yyyy");
    if (toDate && !isDDMMYYYY(toDate)) return canUse(false, "To date must be dd/MM/yyyy");
    if (fromDate && parseDDMMYYYY(fromDate) == null) return canUse(false, "Invalid From date");
    if (toDate && parseDDMMYYYY(toDate) == null) return canUse(false, "Invalid To date");
    if (fromDate && toDate) {
      const f = parseDDMMYYYY(fromDate);
      const t = parseDDMMYYYY(toDate);
      if (t.getTime() < f.getTime()) return canUse(false, "Please select from date less than to date");
    }
    return true;
  }, [filters, canUse]);

  const resetFilters = useCallback((fromDate = "", toDate = "") => {
    setFilters({
      fromDate: fromDate || todayDDMMYYYY(),
      toDate: toDate || todayDDMMYYYY(),
    });
  }, []);

  const getProgressId = useCallback(
    (r, idx) => toInt(r?.fld_vehic_progs_id) || toInt(r?.id) || idx + 1,
    []
  );
  const getProgressDetId = useCallback(
    (r) =>
      toInt(
        r?.fld_vehic_progs_detid ??
          r?.fld_vehic_progsdetailid ??
          r?.fld_progress_detid ??
          r?.progressDetId ??
          r?.detId ??
          0,
        0
      ),
    []
  );

  const checkedCount = useMemo(() => Object.keys(selected).length, [selected]);

  const saveSelectionToLocalStorage = useCallback(
    (vehDetails, progressIds, progressDetIds) => {
      try {
        const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            ...existing,
            vehicleId: vehId,
            vehDetails: vehDetails ?? existing.vehDetails ?? null,
            progressIds: Array.isArray(progressIds) ? progressIds.map((x) => Number(x) || 0) : [],
            progressDetIds: Array.isArray(progressDetIds) ? progressDetIds.map((x) => Number(x) || 0) : [],
            fromDate: filters.fromDate,
            toDate: filters.toDate,
            updatedAt: new Date().toISOString(),
          })
        );
      } catch {
        /* ignore */
      }
    },
    [STORAGE_KEY, filters, vehId]
  );

  const goToBillGenerationWithData = useCallback(
    (vehDetails, progressIds, progressDetIds) => {
      const payload = {
        vehId,
        vehicleId: vehId,
        vehType,
        vehDetails,
        progressIds,
        progressDetIds,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
      };
      sessionStorage.setItem("billGeneration_selectedVehDetails", JSON.stringify(vehDetails || {}));
      sessionStorage.setItem(`billProgress:${vehId}`, JSON.stringify(payload));
      saveSelectionToLocalStorage(vehDetails, progressIds, progressDetIds);
      navigate(`/vehicle/BillGeneration?VehID=${encodeURIComponent(vehId)}&brtype=${encodeURIComponent(vehType)}`, {
        state: payload,
      });
    },
    [vehId, vehType, filters, navigate, saveSelectionToLocalStorage]
  );

  const loadInit = useCallback(async () => {
    if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
    if (!canUse(toInt(branchId), "branchId missing")) return;
    if (!canUse(vehId, "VehID missing in URL. Use ?VehID=79&Type=4")) return;

    try {
      setPageLoading(true);
      const { data } = await axios.get(`${API_BASE}/init`, {
        params: { VehID: vehId, Type: vehType, branchId, loginUserId },
        headers: commonHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Init failed");

      setHeader({
        vehicleNumber: data?.header?.vehicleNumber || data?.details?.regNo || "",
        period: data?.header?.period || "",
        toDate: data?.header?.toDate || "",
        basis: data?.details?.basis || "",
        vehicleType: data?.details?.veh_type || "",
      });
      resetFilters(data?.header?.fromDate || todayDDMMYYYY(), data?.header?.toDateInput || todayDDMMYYYY());

      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const sel = JSON.parse(raw);
          const pids = Array.isArray(sel?.progressIds) ? sel.progressIds : [];
          const pdet = Array.isArray(sel?.progressDetIds) ? sel.progressDetIds : [];
          if (pids.length) {
            const map = {};
            for (let i = 0; i < pids.length; i++) {
              const pid = toInt(pids[i], 0);
              map[pid] = toInt(pdet[i], 0) || 0;
            }
            setSelected(map);
          } else setSelected({});
        } else setSelected({});
      } catch {
        setSelected({});
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Initialization failed");
    } finally {
      setPageLoading(false);
    }
  }, [loginUserId, branchId, vehId, vehType, commonHeaders, canUse, resetFilters, STORAGE_KEY]);

  const loadList = useCallback(async () => {
    if (!validateDates()) return;
    if (!canUse(vehId, "VehID missing in URL")) return;
    try {
      setGridLoading(true);
      setHasSearched(true);
      const { data } = await axios.get(`${API_BASE}/list`, {
        params: {
          VehID: vehId,
          Type: vehType,
          fromDate: filters.fromDate,
          toDate: filters.toDate,
          branchId,
          loginUserId,
        },
        headers: commonHeaders,
      });
      if (!data?.success) {
        setList([]);
        toast.warn(data?.message || "No data found");
        return;
      }
      setHeader((p) => ({
        ...p,
        vehicleNumber: data?.header?.vehicleNumber || p.vehicleNumber,
        period: data?.header?.period || "",
        toDate: data?.header?.toDate || "",
        basis: data?.header?.basis || p.basis,
        vehicleType: data?.header?.vehicleType || p.vehicleType,
      }));
      const rows = data?.list || [];
      setList(rows);
      if (!rows.length) toast.info("No Records found");
    } catch (err) {
      console.error(err);
      setList([]);
      toast.error(err?.response?.data?.message || err.message || "Failed to load list");
    } finally {
      setGridLoading(false);
    }
  }, [validateDates, canUse, vehId, vehType, filters, branchId, loginUserId, commonHeaders]);

  const handleClearFilters = useCallback(() => {
    resetFilters(todayDDMMYYYY(), todayDDMMYYYY());
    setList([]);
    setHasSearched(false);
  }, [resetFilters]);

  const toggleRow = useCallback(
    (row, idx) => {
      const pid = getProgressId(row, idx);
      const detId = getProgressDetId(row) || 0;
      setSelected((p) => {
        const next = { ...p };
        if (next[pid] !== undefined) delete next[pid];
        else next[pid] = detId;
        return next;
      });
    },
    [getProgressId, getProgressDetId]
  );

  const allSelected =
    list.length > 0 && list.every((r, idx) => selected[getProgressId(r, idx)] !== undefined);

  const toggleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelected({});
      return;
    }
    const map = {};
    list.forEach((r, idx) => {
      const pid = getProgressId(r, idx);
      map[pid] = getProgressDetId(r) || 0;
    });
    setSelected(map);
  }, [allSelected, list, getProgressId, getProgressDetId]);

  const handleRefresh = useCallback(async () => {
    setList([]);
    await loadInit();
  }, [loadInit]);

  const handleBack = useCallback(() => {
    navigate(`/vehicle/BillGeneration?VehID=${vehId}&brtype=${vehType}`);
  }, [navigate, vehId, vehType]);

  const onBill = useCallback(async () => {
    const progressIds = Object.keys(selected).map((x) => toInt(x)).filter((x) => x > 0);
    if (!canUse(progressIds.length > 0, "Select at least one row")) return;
    const progressDetIds = progressIds.map((pid) => toInt(selected[pid], 0) || 0);

    try {
      setBillingLoading(true);
      const { data } = await axios.post(
        `${API_BASE}/add`,
        {
          VehID: vehId,
          Type: vehType,
          fromDate: filters.fromDate,
          toDate: filters.toDate,
          selectedIds: progressIds,
          selectedDetIds: progressDetIds,
          branchId,
          loginUserId,
        },
        { headers: commonHeaders }
      );
      if (!data?.success) throw new Error(data?.message || "Add failed");
      toast.success(data?.message || "Bill details calculated");
      goToBillGenerationWithData(data.vehDetails, progressIds, progressDetIds);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Bill error");
    } finally {
      setBillingLoading(false);
    }
  }, [selected, canUse, vehId, vehType, filters, branchId, loginUserId, commonHeaders, goToBillGenerationWithData]);

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  const renderCell = (row, key) => {
    const v = row?.[key];
    if (v === null || v === undefined || v === "") return "-";
    return String(v);
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BILL GENERATION - ADD VEHICLE</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div>
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Bill Generation - Add Vehicle
              </h2>
              <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {branchId || "-"} • User ID: {loginUserId || "-"} • Vehicle ID: {vehId || "-"} • Selected: {checkedCount} • Mode: {vehType === "O" ? "Own" : "Hire"}
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button type="button" onClick={handleRefresh} disabled={isBusy} className={`${ghostBtn} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>
                <FaSyncAlt /> Refresh
              </button>
              <button type="button" onClick={() => setShowFilters((s) => !s)} className={ghostBtn}>
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
              <button type="button" onClick={handleBack} disabled={isBusy} className={`${ghostBtn} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>
                <FaArrowLeft /> Back
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={panelClass}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={panelTitleClass}>Search Criteria</h3>
                <div className="flex gap-2">
                  <button type="button" onClick={handleClearFilters} className={ghostBtn}>
                    Clear
                  </button>
                  <button type="button" onClick={loadList} disabled={isBusy} className={`${primaryBtn} !px-4 !py-2 ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>
                    <FaSearch /> {gridLoading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>From Date</label>
                  <input name="fromDate" value={filters.fromDate} onChange={(e) => setFilters((p) => ({ ...p, fromDate: e.target.value }))} className={fieldClass} placeholder="dd/MM/yyyy" disabled={isBusy} />
                </div>
                <div>
                  <label className={labelClass}>To Date</label>
                  <input name="toDate" value={filters.toDate} onChange={(e) => setFilters((p) => ({ ...p, toDate: e.target.value }))} className={fieldClass} placeholder="dd/MM/yyyy" disabled={isBusy} />
                </div>
                <div>
                  <label className={labelClass}>Vehicle Number</label>
                  <div className={fieldClass}>{header.vehicleNumber || "-"}</div>
                </div>
                <div>
                  <label className={labelClass}>Period</label>
                  <div className={fieldClass}>{header.period || "-"}</div>
                </div>
                <div>
                  <label className={labelClass}>Basis</label>
                  <div className={fieldClass}>{header.basis || "-"}</div>
                </div>
                <div>
                  <label className={labelClass}>Vehicle Type</label>
                  <div className={fieldClass}>{header.vehicleType || "-"}</div>
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {gridLoading || pageLoading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</div>
              </div>
            ) : list.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {hasSearched ? "No Records found" : "Please search to view records"}
                </div>
              </div>
            ) : (
              <table className={`w-full min-w-[1600px] ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                      <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="w-4 h-4 cursor-pointer" title="Select All" />
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                    {columns.map((c) => (
                      <th key={c.key} className="px-6 py-4 text-left text-sm font-semibold whitespace-nowrap">
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {list.map((row, idx) => {
                    const pid = getProgressId(row, idx);
                    const checked = selected[pid] !== undefined;
                    return (
                      <tr
                        key={pid}
                        className={`${
                          idx % 2 === 0
                            ? isDark ? "bg-gray-800" : "bg-white"
                            : isDark ? "bg-gray-750" : "bg-gray-50"
                        } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className="px-6 py-4 text-center">
                          <input type="checkbox" checked={checked} onChange={() => toggleRow(row, idx)} className="w-4 h-4 cursor-pointer" disabled={isBusy} />
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>{idx + 1}</td>
                        {columns.map((c) => (
                          <td key={c.key} className={`px-6 py-4 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {renderCell(row, c.key)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {list.length > 0 && (
            <div className="p-4">
              <button
                type="button"
                onClick={onBill}
                disabled={isBusy || checkedCount === 0}
                className={`${primaryBtn} !px-4 !py-2 ${checkedCount === 0 || isBusy ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <FaFileInvoiceDollar /> {billingLoading ? "Processing..." : "Add to Bill"}
              </button>
            </div>
          )}

          {isBusy && (
            <div className={`mt-3 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              {pageLoading ? "Loading page..." : gridLoading ? "Searching..." : billingLoading ? "Adding to bill..." : "Loading..."}
            </div>
          )}

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default BillAddVehicle;